import type { AssetInfo } from "../net/api";

/** The room's size in metres (spec 2o §2.2); the floor is y = 0, the shelf stands on the north wall (+z). */
export const ROOM = { width: 10, depth: 8, height: 3.2, wall: 0.2 } as const;
/** Eight columns by three rows of 0.6 m cells; each model fits a 0.5 m cube (spec 2o §2.3). */
export const SHELF = { cols: 8, rows: 3, cell: 0.6, slot: 0.5, bottom: 0.4, depth: 0.5 } as const;
export const PER_PAGE = SHELF.cols * SHELF.rows;
/** Where the cells' centres are in z: the shelf's back against the north wall. */
export const SHELF_Z = ROOM.depth / 2 - SHELF.depth / 2;
export const TURNTABLE = { x: 0, z: 0, radius: 0.8, height: 0.15 } as const;
/** Where the player appears, facing the shelf. */
export const ROOM_SPAWN = { x: 0, z: -2.5, yaw: 0 } as const;

/** Rows fill from eye level: the middle row first, then the top, then the bottom. */
const ROW_ORDER = [1, 2, 0] as const;

export interface ShelfCell { asset: AssetInfo; col: number; row: number; pos: { x: number; y: number; z: number } }
export interface ShelfLayout { page: number; pages: number; cells: ShelfCell[] }

/** The top centre of a cell's board; row 0 is the bottom row. */
export function cellPosition(col: number, row: number): { x: number; y: number; z: number } {
  return { x: (col - (SHELF.cols - 1) / 2) * SHELF.cell, y: SHELF.bottom + row * SHELF.cell, z: SHELF_Z };
}

/** One page of the shelf, newest first; the page is clamped into range. */
export function layoutShelf(assets: readonly AssetInfo[], page: number): ShelfLayout {
  const sorted = [...assets].sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at));
  const pages = Math.max(1, Math.ceil(sorted.length / PER_PAGE));
  const p = Math.min(Math.max(Math.trunc(page), 0), pages - 1);
  const cells = sorted.slice(p * PER_PAGE, (p + 1) * PER_PAGE).map((asset, i) => {
    const row = ROW_ORDER[Math.floor(i / SHELF.cols)]!, col = i % SHELF.cols;
    return { asset, col, row, pos: cellPosition(col, row) };
  });
  return { page: p, pages, cells };
}

type V3 = { x: number; y: number; z: number };

/** Scale and position for a holder whose model spans min..max so it fits a size-metre cube standing at `at`. */
export function fitTransform(min: V3, max: V3, at: V3, size: number): { scale: number; x: number; y: number; z: number } {
  const ext = Math.max(max.x - min.x, max.y - min.y, max.z - min.z);
  const scale = ext > 1e-6 ? size / ext : 1;
  return { scale, x: at.x - ((min.x + max.x) / 2) * scale, y: at.y - min.y * scale, z: at.z - ((min.z + max.z) / 2) * scale };
}
